<template>
  <div class="mb-4 fullWidth" id="PickupDetailsCard">
    <v-card-title primary-title>
      <div class="headline mb-0">
        <template v-if="isDineIn">
          {{ $vuetify.t('$vuetify.order.orderDetails') }}
        </template>
        <template v-else>
          {{ isPickUp ? $vuetify.t('$vuetify.order.pickUpDetails') : $vuetify.t('$vuetify.account.deliveryDetails') }}
        </template>
      </div>
    </v-card-title>

    <div v-if="isPickUp && pickupInstructions" id="pickupInstructions" v-html="pickupInstructions"></div>

    <v-layout row wrap class="checkout-form">
      <!-- Group order: show scheduled time; host can click to edit -->
      <template v-if="!isDineIn && $store.getters['groupOrder/isInGroupOrder']">
        <v-flex xs12>
          <div
            :class="['go-time-display', { 'go-time-display--editable': $store.state.groupOrder.isHost }]"
            @click="$store.state.groupOrder.isHost && SET_PICKUP_DELIVERY_EDIT_DIALOG(true)"
          >
            <template v-if="groupOrderScheduledDate">
              <div class="go-time-col">
                <div class="go-time-label">{{ isPickUp ? $vuetify.t('$vuetify.order.pickUpDay') : $vuetify.t('$vuetify.order.deliveryDay') }}</div>
                <div class="go-time-value">{{ groupOrderScheduledDay }}</div>
              </div>
              <div class="go-time-col">
                <div class="go-time-label">{{ isPickUp ? $vuetify.t('$vuetify.order.pickUpTime') : $vuetify.t('$vuetify.order.deliveryTime') }}</div>
                <div class="go-time-value">{{ groupOrderScheduledTime }}</div>
              </div>
            </template>
            <template v-else>
              <div class="go-time-col">
                <div class="go-time-label">{{ isPickUp ? $vuetify.t('$vuetify.order.pickUpTime') : $vuetify.t('$vuetify.order.deliveryTime') }}</div>
                <div class="go-time-value">{{ $vuetify.t('$vuetify.groupOrder.asap') }}</div>
              </div>
            </template>
            <v-icon v-if="$store.state.groupOrder.isHost" class="go-time-edit-icon" small>edit</v-icon>
          </div>
        </v-flex>
      </template>

      <template v-else-if="!isDineIn && getStoreAvailableOrderDays && getStoreAvailableOrderDays.length && hoursByDay">
        <v-flex xs6 class="pr-1">
        <v-select
          name="order_day"
          :error="this.scheduledDay === 'default'"
          v-validate="'required'"
          :data-vv-as="isPickUp ? $vuetify.t('$vuetify.order.pickUpDay') : $vuetify.t('$vuetify.order.deliveryDay')"
          :error-messages="errors.collect('order_day')"
          v-model="selectedDay"
          :label="isPickUp ? $vuetify.t('$vuetify.order.pickUpDay') : $vuetify.t('$vuetify.order.deliveryDay')"
          :items="getStoreAvailableOrderDays"
          item-value="id"
          item-text="label"
          @change="dayChanged()"
          :disabled="!allowSchedulingOrders || _isNullOrOne(getStoreAvailableOrderDays)"
        />
        </v-flex>

        <v-flex xs6 class="pl-1">
          <v-select
            name="order_time"
            v-validate="'required'"
            :data-vv-as="isPickUp ? $vuetify.t('$vuetify.order.pickUpTime') : $vuetify.t('$vuetify.order.deliveryTime')"
            :error-messages="errors.collect('order_time')"
            v-model="scheduledTime"
            :label="isPickUp ? $vuetify.t('$vuetify.order.pickUpTime') : $vuetify.t('$vuetify.order.deliveryTime')"
            :items="hoursByDay"
            item-value="id"
            item-text="label"
            :disabled="!allowSchedulingOrders || _isNullOrOne(hoursByDay)"
          />
        </v-flex>
      </template>

      <v-flex xs12>
        <v-form v-if="isDelivery">
          <v-layout row wrap>
            <v-flex xs6>
              <v-text-field
                name="unitNumber"
                v-model="unitNumber"
                :label="$vuetify.t('$vuetify.checkout.unitNumber')"
              />
            </v-flex>

            <v-flex xs12 class="title text-xs-left" style="margin: 25px 0px">
              {{ getDeliveryAddressFormatted }}
            </v-flex>
          </v-layout>
        </v-form>

        <v-form v-if="!isDisableSpecialRequest()">
          <v-textarea
            v-model="orderNotes"
            rows="3"
            :label="$vuetify.t('$vuetify.order.orderNotes')"
            :placeholder="$vuetify.t('$vuetify.checkout.orderHints')"
          ></v-textarea>
        </v-form>

        <div v-if="isDelivery" class="text-xs-left">
          <v-form style="margin: 10px 10px 20px 0px">
            <div class="title" style="font-size: 18px !important; margin-bottom: 5px">
              {{ $vuetify.t('$vuetify.checkout.deliveryOptions') }}
            </div>

            <template v-for="option in deliveryMessageOptions">
              <v-btn v-if="getStoreDeliveryProvider != 'TINY_MILE' || option.key == 'meet_outside'"
                :key="option.key"
                :color="deliveryMessage == option.val ? 'primary' : null"
                @click="deliveryMessage = deliveryMessage != option.val ? option.val : null"
                class="deliveryMessageButton"
              >
                <v-icon size="22" style="width: 30px">
                  {{ option.icon }}
                </v-icon>

                {{ $vuetify.t(`$vuetify.checkout.deliveryMessage.${option.key}`) }}
              </v-btn>
            </template>

            <div v-if="getStoreDeliveryProvider == 'TINY_MILE'">
                {{ $vuetify.t('$vuetify.storePage.deliveryProvider.deliveryInstructions') }}
            </div>
          </v-form>

          <v-form>
            <v-textarea
              v-model="deliveryNotes"
              rows="3"
              :label="$vuetify.t('$vuetify.checkout.deliveryInstructions')"
              :placeholder="$vuetify.t('$vuetify.checkout.deliveryHints')"
            ></v-textarea>
          </v-form>
        </div>
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
import get from 'lodash/get';

import { mapMutations, mapGetters } from 'vuex';

import { matchTranslation, ensureDateObject, isNullOrEmpty, getDisplayLocale } from "@/services/util.service"

export default {
  data: () => ({
    deliveryMessageOptions: [
      {key: 'leave_at_door', val: 'Leave At Door', icon: 'meeting_room'},
      {key: 'meet_at_door', val: 'Meet At Door', icon: 'accessibility_new'},
      {key: 'meet_outside', val: 'Meet Outside', icon: 'directions_car'}
    ]
  }),

  computed: {
    selectedDay: {
      get() {
        const currentDay = this.scheduledDay
        if (!currentDay) return null
        
        const matchingDay = this.getStoreAvailableOrderDays.find(day => 
          this.formatDateForComparison(day.id) === this.formatDateForComparison(currentDay)
        )
        return matchingDay && matchingDay.id || null
      },
      set(value) {
        this.scheduledDay = value
      }
    },
    orderNotes: {
      get() {
        return get(this.getCheckoutInfo, 'orderNotes', '')
      },
      set(value) {
        this.SET_CHECKOUT_INFO({
          ...this.getCheckoutInfo,
          orderNotes: value
        })
      }
    },
    deliveryNotes: {
      get() {
        return get(this.getCheckoutInfo, 'deliveryNotes', '')
      },
      set(value) {
        this.SET_CHECKOUT_INFO({
          ...this.getCheckoutInfo,
          deliveryNotes: value
        })
      }
    },
    scheduledDay: {
      get() {
        return get(this.getCheckoutInfo, 'scheduledDay', '')
      },
      set(value) {
        this.SET_CHECKOUT_INFO({
          ...this.getCheckoutInfo,
          scheduledDay: value
        })
      }
    },
    scheduledTime: {
      get() {
        return get(this.getCheckoutInfo, 'scheduledTime', '')
      },
      set(value) {
        this.SET_CHECKOUT_INFO({
          ...this.getCheckoutInfo,
          scheduledTime: value
        })
      }
    },
    unitNumber: {
      get() {
        return get(this.getCheckoutInfo, 'unitNumber', '')
      },
      set(value) {
        this.SET_CHECKOUT_INFO({
          ...this.getCheckoutInfo,
          unitNumber: value
        })
      }
    },
    deliveryMessage: {
      get() {
        return get(this.getCheckoutInfo, 'deliveryMessage', true)
      },
      set(value) {
        this.SET_CHECKOUT_INFO({
          ...this.getCheckoutInfo,
          deliveryMessage: value
        })
      }
    },

    pickupInstructions() {
      return matchTranslation(get(this.getStore, 'settings.pickupSettings.instructions.translations', []))
    },

    getDeliveryAddressFormatted() {
      return get(this.getDeliveryAddressResult, 'formatted_address', '')
    },

    hoursByDay() {
      if (this.getStoreAvailableOrderDays && this.scheduledDay) {
        const searchDay = ensureDateObject(this.scheduledDay).getDate()
        const matchedDay = this.getStoreAvailableOrderDays.find(day => (new Date(day.id)).getDate() == searchDay)

        return matchedDay ? matchedDay.times : []
      }
    },

    allowSchedulingOrders() {
      if (this.$store.getters['groupOrder/isInGroupOrder']) return false;
      return !this.isDelivery || this.getStoreDeliveryProvider !== 'TINY_MILE'
    },
    groupOrderScheduledDate() {
      const t = this.$store.state.groupOrder && this.$store.state.groupOrder.group && this.$store.state.groupOrder.group.scheduledTime;
      if (!t) return null;
      const d = new Date(t);
      return Number.isNaN(d.getTime()) ? null : d;
    },
    groupOrderScheduledDay() {
      if (!this.groupOrderScheduledDate) return '';
      const tz = (this.getStore && this.getStore.timeZone) || undefined;
      return this.groupOrderScheduledDate.toLocaleDateString(getDisplayLocale(this.$vuetify.lang.current), { weekday: 'short', month: 'short', day: 'numeric', timeZone: tz });
    },
    groupOrderScheduledTime() {
      if (!this.groupOrderScheduledDate) return '';
      const tz = (this.getStore && this.getStore.timeZone) || undefined;
      return this.groupOrderScheduledDate.toLocaleTimeString(getDisplayLocale(this.$vuetify.lang.current), { hour: 'numeric', minute: '2-digit', timeZone: tz });
    },

    ...mapGetters([
      'getDeliveryAddressResult',
      'isDelivery',
      'isPickUp',
      'isDineIn',
      'getStore',
      'getDeliveryAddress',
      'getStoreDeliveryProvider',
      'getCheckoutInfo',
      'getStoreAvailableOrderDays',
      'getOrderType'
    ])
  },

  methods: {
    formatDateForComparison(date) {
      if (!date) return null
      try {
        const d = new Date(date)
        if (isNaN(d.getTime())) {
          return null
        }
        return d.toISOString().split('T')[0]
      } catch (e) {
        console.warn('Invalid date:', date)
        return null
      }
    },
    _isNullOrEmpty(val) {
      return isNullOrEmpty(val)
    },
    _isNullOrOne(val) {
      return this._isNullOrEmpty(val) || val.length <= 1
    },

    setDayAndTimeIfOnlyOne() {
      const daysAvailable = this.getStoreAvailableOrderDays
      const scheduleTakeoutDatetimeManually = get(this.getStore, 'settings.orderSettings.scheduleTakeoutDatetimeManually', null)
    
      if (scheduleTakeoutDatetimeManually && this.getOrderType !== 'DINE_IN') {
        daysAvailable.unshift({
          dow: 0,
          id: 'default',
          label: 'Please select a date'
        }) 
      }

      if (daysAvailable.length) {
        const dayId = daysAvailable[0].id
        const timeId = get(daysAvailable, '[0].times[0].id')
        
        // Add safe date parsing
        let formattedDayId
        try {
          formattedDayId = typeof dayId === 'string' ? dayId : new Date(dayId).toISOString()
        } catch (e) {
          console.warn('Invalid date format:', dayId)
          formattedDayId = dayId
        }
        
        this.SET_CHECKOUT_INFO({
          ...this.getCheckoutInfo,
          scheduledDay: formattedDayId,
          scheduledTime: timeId
        })
      }
    },

    dayChanged() {
      this.scheduledTime = ''

      this.$nextTick(() => {
        // Since we disable the select when only 1 option is available we need to set it by default
        if (this.hoursByDay && this.hoursByDay.length == 1)
          this.scheduledTime = this.hoursByDay[0]
      })
    },

    isDisableSpecialRequest() {
      return get(this.getStore, 'settings.orderSettings.disableSpecialRequestforItem', false)
    },
   
    ...mapMutations([ 'SET_CHECKOUT_INFO', 'SET_PICKUP_DELIVERY_EDIT_DIALOG' ])
  },


mounted() {
  this.$nextTick(() => {
    if (this.getStoreAvailableOrderDays && this.getStoreAvailableOrderDays.length) {
      if (!this.scheduledDay || !this.scheduledTime) {
        this.setDayAndTimeIfOnlyOne()
      }
    }
  })
},


  watch: {
    getStoreAvailableOrderDays: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.length) {
          if (!this.scheduledDay || !this.scheduledTime) {
            this.setDayAndTimeIfOnlyOne()
          }
        }
      }
    },
    '$vuetify.lang.current'() {
      // Need to manually trigger an update when switching languages to update the fields name
      // Unfortunatly this doesn't seem to have a global affect so needs to
      // be included in all pages that use vee-validate
      this.$nextTick(() => {
        this.$validator.validateAll();
      })
    }
  }
}
</script>

<style scoped>
  #PickupDetailsCard {
    /* border: var(--card-border) !important;
    border-radius: var(--card-border-radius); */
  }

  .headline {
    font-size: var(--text-size-2) !important;
  }

  #pickupInstructions {
    font-size: var(--text-size-1) !important;

    text-align: left;
    margin: 0px 20px 10px;

    font-size: 16px;
    font-weight: 600;
  }

  .deliveryMessageButton {
    font-size: 13px;
    width: 145px;
    margin: 0px 15px 10px 0px;
    padding: 0px
  }

  .go-time-display {
    display: flex;
    align-items: center;
    gap: 32px;
    padding: 8px 0 12px;
    border-bottom: 1px solid rgba(0,0,0,0.12);
    margin-bottom: 8px;
  }

  .go-time-display--editable {
    cursor: pointer;
    border-radius: 6px;
    transition: background 0.15s;
    padding: 8px 8px 12px;
    margin: 0 -8px 8px;
  }

  .go-time-display--editable:hover {
    background: rgba(0,0,0,0.04);
  }

  .go-time-col {
    flex: 1;
  }

  .go-time-label {
    text-align: left;
    font-size: 12px;
    color: rgba(0,0,0,0.54);
    margin-bottom: 2px;
  }

  .go-time-value {
    text-align: left;
    font-size: 16px;
    color: rgba(0,0,0,0.87);
  }

  .go-time-edit-icon {
    color: rgba(0,0,0,0.38) !important;
    flex-shrink: 0;
  }
</style>
